import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { authorizedRequest } from "../../api/authorizedRequest";
import { ApplicationDetailsModal } from "./ApplicationDetailsModal";
import { ApplicationHistoryModal } from "./ApplicationHistoryModal";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

interface Application {
  id: string;
  candidateName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthDate: string;
  streetAddress: string;
  addressLine2?: string;
  city: string;
  stateOrProvince: string;
  postalCode: string;
  jobAppliedFor: string;
  referenceName1: string;
  referenceTitle1: string;
  referenceRelationship1: string;
  referenceEmail1: string;
  referencePhoneNumber1: string;
  referenceName2: string;
  referenceTitle2: string;
  referenceRelationship2: string;
  referenceEmail2: string;
  referencePhoneNumber2: string;
  previousWorkPlace1?: string;
  previousJobTitle1?: string;
  previousJobStartDate1?: string;
  previousJobEndDate1?: string;
  previousJobPhoneNumber1?: string;
  previousWorkPlace2?: string;
  previousJobTitle2?: string;
  previousJobStartDate2?: string;
  previousJobEndDate2?: string;
  previousJobPhoneNumber2?: string;
  creationTime: string;
  lastModificationTime?: string;
  status: number;
  resumePath?: string;
}

export const ApplicationList: React.FC = () => {
  const location = useLocation();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filterStatus, setFilterStatus] = useState<string | number>("-1");
  const [filterApplicantName, setFilterApplicantName] = useState<string>("");
  const [filterFromDate, setFilterFromDate] = useState<string>("");
  const [filterToDate, setFilterToDate] = useState<string>("");

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);

  const fetchApplications = async (page: number, size: number) => {
    setLoading(true);
    try {
      const response = await authorizedRequest({
        url: '/JobApplication',
        method: 'GET',
        params: {
          skipCount: (page-1) * size,
          maxResultCount: size,
          status: Number(filterStatus),
          applicantName: filterApplicantName,
          fromDate: filterFromDate,
          toDate: filterToDate,
          sorting: sortColumn ? `${sortColumn} ${sortDirection}` : undefined,
        },
      });
      setApplications(response.data.items);
      setTotalRows(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching applications:", error);
      Swal.fire("Error", "Failed to fetch applications.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(currentPage, perPage);
  }, [
    currentPage,
    perPage,
    filterStatus,
    filterApplicantName,
    filterFromDate,
    filterToDate,
    sortColumn,
    sortDirection
  ]);

  // Handle automatic opening of ApplicationDetailsModal when redirected from email token validation
  useEffect(() => {
    const locationState = location.state as { 
      openApplicationId?: string; 
      fromEmailToken?: boolean; 
    } | null;

    if (locationState?.openApplicationId && locationState?.fromEmailToken) {
      // Set the application ID and show modal
      setSelectedApplicationId(locationState.openApplicationId);
      setShowDetailsModal(true);
      
      // Clear the location state to prevent reopening on refresh
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  }, [location.state]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleViewDetails = (row: Application) => {
    setSelectedApplicationId(row.id);
    setShowDetailsModal(true);
  };

  const handleViewHistory = (row: Application) => {
    setSelectedApplicationId(row.id);
    setShowHistoryModal(true);
  };

  const handleStatusUpdate = async (
    applicationId: string,
    newStatus: string,
    dateHired: string,
    position: string,
    wageScale: string,
    firstWeekOfWork: string,
    notes: string,
    sageEmployeeId: string
  ) => {
    try {
      const isApproval = newStatus.toLowerCase().includes('approved');
      
      const requestData: any = {
        applicationId,
        newStatus,
        notes
      };

      // Only include hiring fields for approval
      if (isApproval) {
        requestData.dateHired = dateHired;
        requestData.position = position;
        requestData.wageScale = wageScale;
        requestData.firstWeekOfWork = firstWeekOfWork;
        requestData.sageEmployeeId = sageEmployeeId;
      }

      await authorizedRequest({
        url: `/JobApplication/${applicationId}/updateStatus`,
        method: 'POST',
        data: requestData,
      });
      Swal.fire(
        "Success",
        "Application status updated successfully!",
        "success"
      );
      fetchApplications(currentPage, perPage); // Refresh list
      setShowDetailsModal(false);
    } catch (error: any) {
      console.error("Error updating status:", error);
      
      // Handle server error responses
      if (error.response && error.response.status === 500) {
        const errorData = error.response.data;
        const errorMessage = errorData?.details || errorData?.message || "You cannot Approve or Reject this application.";
        Swal.fire("Error.", 'You cannot Approve or Reject this application.\n' + errorMessage, "error");
      } else if (error.response && error.response.data) {
        // Handle other server errors
        const errorMessage = error.response.data.details || error.response.data.message || "An error occurred while updating the application status.";
        Swal.fire("Error", errorMessage, "error");
      } else {
        // Handle network or other errors
        Swal.fire("Error", "An error occurred while updating the application status.", "error");
      }
    }
  };

  const statusMap: { [key: number]: string } = {
    0: "Applied",
    1: "HR-ManagerApproved",
    2: "HR-ManagerRejected",
    3: "CFO-Approved",
    4: "CFO-Rejected",
  };

  const columns = [
    // {
    //     name: 'Id',
    //     selector: (row: Application) => row.id,
    //     sortable: true,
    // },
    {
      name: "Applicant Name",
      selector: (row: Application) => row.firstName + " " + row.lastName,
      sortable: true,
      sortField: "FirstName",
      width: "20%",
    },
    {
      name: "Applicant Email",
      selector: (row: Application) => row.email,
      sortable: true,
      sortField: "Email",
      width: "20%",
    },
    {
      name: "Applicant Phone",
      selector: (row: Application) => row.phoneNumber,
      sortable: true,
      sortField: "PhoneNumber",
      width: "20%",
    },
    {
      name: "Status",
      selector: (row: Application) =>
        statusMap[row.status as unknown as number] || row.status,
      sortable: true,
      sortField: "Status",
      width: "15%",
    },
    {
      name: "Applied Date",
      selector: (row: Application) =>
        new Date(row.creationTime).toLocaleDateString(),
      sortable: true,
      sortField: "CreationTime",
      width: "10%",
    },
    {
      name: "Actions",
      cell: (row: Application) => (
        <div className="dropdown">
          <button
            className="btn btn-secondary btn-sm dropdown-toggle"
            type="button"
            id={`dropdownMenuButton-${row.id}`}
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Actions
          </button>
          <div
            className="dropdown-menu"
            aria-labelledby={`dropdownMenuButton-${row.id}`}
          >
            <button
              className="dropdown-item"
              onClick={() => handleViewDetails(row)}
            >
              View
            </button>
            {/* <button className="dropdown-item" onClick={() => handleEdit(row)}>Edit</button> */}
            <button
              className="dropdown-item text-danger"
              onClick={() => handleViewHistory(row)}
            >
              History
            </button>
          </div>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "15%",
    },
  ];

  const [filterOpen, setFilterOpen] = useState(false);

  const handleApplyFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchApplications(1, perPage); // Reset to first page on filter
  };

  const handleClearFilter = () => {
    setFilterStatus('-1');
    setFilterApplicantName('');
    setFilterFromDate('');
    setFilterToDate('');
    setCurrentPage(1);
    setSortColumn('CreationTime');
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#343a40", // dark background
        color: "white",
      },
    },
    headCells: {
      style: {
        color: "white", // light foreground for text and sort icons
        fontWeight: "bold",
      },
    },
  };

  return (
    <div className="pd-ltr-20">
      <div className="card-box pd-20 height-100-p mb-30">
        <div className="row align-items-center">
          <div className="col-md-12">
            <h4 className="font-20 weight-500 mb-10 text-capitalize">
              Job Applications
            </h4>
          </div>
        </div>
      </div>
      <div className="accordion mb-3" id="filterAccordion">
        <div className="card">
          <div className="card-header p-0" id="headingFilter">
            <h2 className="mb-0">
              <button
                className="btn btn-link btn-block text-left"
                type="button"
                onClick={() => setFilterOpen(!filterOpen)}
                aria-expanded={filterOpen}
                aria-controls="collapseFilter"
              >
                Filter
              </button>
            </h2>
          </div>
          <div
            id="collapseFilter"
            className={`collapse${filterOpen ? " show" : ""}`}
            aria-labelledby="headingFilter"
            data-parent="#filterAccordion"
          >
            <div className="card-body">
              <form onSubmit={handleApplyFilter} autoComplete="off">
                <div className="row">
                  <div className="col-md-3 mb-2">
                    <select
                      className="form-control"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      value={filterStatus}
                    >
                      <option value="-1" selected>All Statuses</option>
<option value="0">Applied</option>
<option value="1">HR-Manager Approved</option>
<option value="2">HR-Manager Rejected</option>
<option value="3">CFO Approved</option>
<option value="4">CFO Rejected</option>
                    </select>
                  </div>
                  <div className="col-md-3 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Applicant Name"
                      value={filterApplicantName}
                      onChange={(e) => setFilterApplicantName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <input
                      type="date"
                      className="form-control"
                      value={filterFromDate}
                      onChange={(e) => setFilterFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <input
                      type="date"
                      className="form-control"
                      value={filterToDate}
                      onChange={(e) => setFilterToDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 text-right">
                    <button type="submit" className="btn btn-secondary mt-2 mr-2">
                      Apply Filter
                    </button>
                    <button type="button" className="btn btn-outline-secondary mt-2" onClick={handleClearFilter}>
                      Clear Filter
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="card-box mb-30">
        <div className="min-height-200px">
          <div className="pd-20">
            <DataTable
              columns={columns}
              customStyles={customStyles}
              data={applications}
              progressPending={loading}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              highlightOnHover
              pointerOnHover
              sortServer
       onSort={(column, sortDirection) => {
  setSortColumn(column.sortField || "");
  setSortDirection(sortDirection);
}}
            />
          </div>
        </div>
      </div>
      {selectedApplicationId && (
        <ApplicationDetailsModal
          show={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          applicationId={selectedApplicationId}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
      {selectedApplicationId && (
        <ApplicationHistoryModal
          show={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          applicationId={selectedApplicationId}
        />
      )}
    </div>
  );
};
